import { isNil } from 'lodash';
import { RelatedEntityField } from '@shared/data/entity/fields/RelatedEntityField';
import { useWorkflowEntitiesByIds, useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isRelatedCardComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { FieldRecord } from '../../../types';
import { LeafEntity } from './types';

interface UseRelatedCardLookupFieldsParams {
  component: API.RelatedCardLookupComponent;
  fields: FieldRecord;
  workflowTemplateId: string;
}

/**
 * Resolves the leaf component to render and the entities whose values should be rendered
 * for a related card lookup. Supports one extra hop: when the looked-up component is itself
 * a related card, it resolves the secondary lookup component on the nested workflow and the
 * nested related entities (one per reference, each tracking the level-1 entity that references
 * it). The leaf value is then rendered the same as a first-level lookup.
 */
export function useRelatedCardLookupFields({
  component,
  fields,
  workflowTemplateId,
}: UseRelatedCardLookupFieldsParams): {
  leafComponent: API.AnyComponent | undefined;
  leafEntities: LeafEntity[];
} {
  // Top-level template -> related card component -> looked-up template + component.
  const { workflowTemplate: topLevelWorkflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const topLevelRelatedCardComponent = topLevelWorkflowTemplate?.components
    .filter(isRelatedCardComponent)
    .find(c => c.id === component.relatedCardComponentId);
  const lookupWorkflowTemplateId = topLevelRelatedCardComponent?.workflowTemplateId;
  const { workflowTemplate: lookupWorkflowTemplate } =
    useWorkflowTemplate(lookupWorkflowTemplateId);
  const lookupComponent = lookupWorkflowTemplate?.components.find(
    c => c.id === component.lookupComponentId,
  );
  const isNestedLookup = !isNil(lookupComponent) && isRelatedCardComponent(lookupComponent);

  // Level 1: the related card entities referenced by the top-level entity. Use the refs loaded
  // with the form and only query the ids that are missing from them. Querying the level-1
  // entities also returns their related cards as refs, which we reuse as the level-2 pool.
  const relatedField = fields[component.relatedCardComponentId] as RelatedEntityField | undefined;
  const lookupIds = relatedField?.items.map(i => i.id) ?? [];
  const lookupEntitiesFromRefs = relatedField?.relatedEntities ?? [];
  const missingLookupIds = lookupIds.filter(id => !lookupEntitiesFromRefs.some(e => e.id === id));
  const { workflowEntities: fetchedLookupEntities, workflowEntityRefs: lookupNestedRefs } =
    useWorkflowEntitiesByIds({
      workflowTemplateId: lookupWorkflowTemplateId,
      workflowEntityIds: missingLookupIds,
    });
  const lookupEntities = resolveEntities(lookupIds, [
    lookupEntitiesFromRefs,
    fetchedLookupEntities,
  ]);

  // Level 2 (optional): when the looked-up component is itself a related card, hop once more
  // into the nested workflow. Look the nested entities up in the refs first (those loaded with
  // the form plus any returned by the level-1 query) and only query the ids still missing.
  const secondaryLookupWorkflowTemplateId = isNestedLookup
    ? lookupComponent.workflowTemplateId
    : undefined;
  const { workflowTemplate: secondaryLookupWorkflowTemplate } = useWorkflowTemplate(
    secondaryLookupWorkflowTemplateId,
  );
  const secondaryLookupComponent = secondaryLookupWorkflowTemplate?.components.find(
    c => c.id === component.secondaryLookupComponentId,
  );

  // One match per (level-1 entity, secondary card) reference, in first-seen order. We do NOT
  // dedupe: the same secondary card renders once for every level-1 entity that references it.
  const secondaryMatches = isNestedLookup
    ? lookupEntities.flatMap(source =>
        relatedEntityIds(source.fields?.[component.lookupComponentId]).map(id => ({ source, id })),
      )
    : [];
  // Unique secondary card ids, so each entity is looked up / queried only once.
  const secondaryLookupIds = [...new Set(secondaryMatches.map(m => m.id))];
  const secondaryEntitiesFromRefs = lookupNestedRefs.filter(e => secondaryLookupIds.includes(e.id));
  const missingSecondaryIds = secondaryLookupIds.filter(
    id => !secondaryEntitiesFromRefs.some(e => e.id === id),
  );
  const { workflowEntities: fetchedSecondaryEntities } = useWorkflowEntitiesByIds({
    workflowTemplateId: secondaryLookupWorkflowTemplateId,
    workflowEntityIds: missingSecondaryIds,
  });
  const secondaryLookupEntities = resolveEntities(secondaryLookupIds, [
    secondaryEntitiesFromRefs,
    fetchedSecondaryEntities,
  ]);

  if (isNestedLookup) {
    // One leaf per match so duplicates render as separate fields, each showing its source path.
    const leafEntities: LeafEntity[] = secondaryMatches
      .map(({ source, id }) => {
        const entity = secondaryLookupEntities.find(e => e.id === id);
        return entity ? { entity, sources: [source] } : null;
      })
      .filter(notNil);
    return { leafComponent: secondaryLookupComponent, leafEntities };
  }
  return {
    leafComponent: lookupComponent,
    leafEntities: lookupEntities.map(entity => ({ entity, sources: [] })),
  };
}

/** Resolves entities for the given ids from the provided pools, preferring earlier pools. */
function resolveEntities(ids: string[], pools: API.WorkflowEntity[][]): API.WorkflowEntity[] {
  return ids.map(id => pools.map(pool => pool.find(e => e.id === id)).find(notNil)).filter(notNil);
}

function relatedEntityIds(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(item => item?.id).filter((id): id is string => typeof id === 'string');
}
